import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';



import { getDraftService } from '@asd-stan/draft/infrastructure/getters';
import { ToasterStatus } from '@asd-stan/shell/domain/toaster.service';
import { getToasterService } from '@asd-stan/shell/infrastructure/getters';
import { CreateNewStandardEAFormGeneral } from '@asd-stan/standard/components/nwp/forms/ea/ea-general';
import { getStandardService } from '@asd-stan/standard/infrastructure/getters';
import { BreadCrumbs } from '@asd-stan/ui-kit/components/bread-crumbs/bread-crumbs';
import { Button } from '@asd-stan/ui-kit/components/button/button';
import { Flex } from '@asd-stan/ui-kit/components/utility/flex';
import { InputGroup } from '@asd-stan/user/components/layout/input-group/input-group';
import { PageTitle } from '@components/page-title/page-title';
import { MarkedText } from '@components/utility/marked-text';
import { Form, Formik, FormikValues } from 'formik';
import CreateNewStandardEAPatents from 'src/standard/components/nwp/forms/ea/ea-patents';
import CreateNewStandardEAReferences from 'src/standard/components/nwp/forms/ea/ea-references';
import CreateNewStandardEATimeline from 'src/standard/components/nwp/forms/ea/ea-timeline';
import { CreateNewStandardEAFormScopeJustification } from 'src/standard/components/nwp/forms/ea/scopeJustification';
import { Attachments } from 'src/standard/components/nwp/forms/prEnTr/attachments';
import { DraftRemarks } from 'src/standard/components/nwp/forms/prEnTr/draft-remarks';
import { Experts } from 'src/standard/components/nwp/forms/prEnTr/experts';
import { StandardDraft } from 'src/standard/components/nwp/forms/prEnTr/standard-draft';
import { NWPEASchema, emptySchema } from 'src/standard/validation-schemas/nwp.schema';



import { makeDraftNwpCreation } from './draft-edit-nwp';



import { StyledButtonGroup } from '@asd-stan/ui-kit/components/utility/button-group.styled';
import { ContentContainer } from '@asd-stan/ui-kit/components/utility/content-container.styled';


const initialValues = {
	form: 'EA',
	registrationNumber: '',
	revisionId: { label: '', value: '' },
	standard: '',
	stanNumber: '',
	edition: '',
	languages: null,
	typeIds: [],
	pages: '',
	domainCodes: [],
	workingGroupCodes: [],
	scope: '',
	originator: {},
	radioForm: true,
	isAsdOriginator: false,
	revisionJustification: '',
	technicalChanges: [],
	knownPatentIssue: false,
	patentReferences: '',
	tags: [],
	classification: [],
	standardTitleEN: '',
	remark: '',
	attachments: [],
	newOriginator: {
		firstName: '',
		lastName: '',
		email: '',
		phone: '',
		company: null,
		companyCountry: null,
		position: [],
	},
	existOriginator: null,
	existEmailOriginatorCredentials: null,
	existPhoneOriginatorCredentials: null,
	showReplaceStandard: false,
	enData: {
		name: '',
		publicationDate: null,
		title: '',
	},
};

export const CreateNwpEa: React.FC = observer(() => {
	const navigation = useNavigate();

	const { t } = useTranslation();

	const toasterService = getToasterService();
	const standardService = getStandardService();
	const draftService = getDraftService();

	const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(false);
	const [isSaveClicked, setIsSaveClicked] = useState<boolean>(false);

	useEffect(() => {
		const createDraft = async () => {
			draftService.remarks = null;
			draftService.singleDraft = null;
			draftService.draftId = null;
			await draftService.createDraft();
			draftService.getDraftsCount();
		};

		setIsSaveDisabled(true);
		createDraft();
		setIsSaveDisabled(false);

		if (standardService.singleStandard !== null) {
			standardService.clearSingleStandard();
		}
	}, []);

	const goToPreviousPage = () => {
		if (draftService.draftId) {
			draftService.singleDraft = null;
			standardService.clearSingleStandard();
			return navigation(`/standards/draft/${draftService.draftId}/general`);
		}
		navigation(-1);
	};

	const onSubmit = async (values: FormikValues) => {
		try {
			setIsSaveDisabled(true);

			if (draftService.draftId) {
				const creation = makeDraftNwpCreation(values, draftService);
				await standardService.updateDraft(draftService.draftId, creation);
			}
			if (isSaveClicked) {
				if (draftService.draftId) {
					const id = await standardService.moveDraftToNWP(draftService.draftId);
					navigation(`/standards/detailed/${id}/general`);
				}
				toasterService.showToast(
					ToasterStatus.success,
					standardService.singleStandard
						? t('standard.createNWP.toaster.successfullUpdate')
						: t('standard.createNWP.toaster.successfullCreate')
				);
			}

			setIsSaveDisabled(false);
			setIsSaveClicked(false);
		} catch (error) {
			setIsSaveDisabled(false);
			setIsSaveClicked(false);
		}
	};

	return (
		<Formik
			validationSchema={isSaveClicked ? NWPEASchema : emptySchema}
			initialValues={initialValues}
			onSubmit={onSubmit}
			validateOnChange
			validateOnBlur={false}>
			{({ handleSubmit, validateForm }) => {
				return (
					<ContentContainer>
						<BreadCrumbs />
						<Flex $justify="space-between" $align="center">
							<PageTitle title={t('standard.createNWPea.title')} />
							<StyledButtonGroup>
								<Button
									disabled={isSaveDisabled}
									secondary
									title={t('standard.createNWP.buttons.cancel')}
									onClick={goToPreviousPage}
								/>
								<Button
									disabled={isSaveDisabled}
									title={t('standard.createNWP.buttons.save')}
									onClick={async () => {
										setIsSaveClicked(true);

										// Wait until Formik re-renders with the active validation schema.
										await new Promise<void>(resolve => {
											requestAnimationFrame(() => resolve());
										});

										const errors = await validateForm();
										if (errors && !(Object.keys(errors).length === 0)) {
											toasterService.showToast(
												ToasterStatus.error,
												t('standard.createNWP.toaster.incorrectFields')
											);
											return;
										}

										try {
											await handleSubmit();
										} catch (error) {
											setIsSaveClicked(false);
										}
									}}
								/>
							</StyledButtonGroup>
						</Flex>

						{standardService.singleStandard && (
							<>
								{standardService.singleStandard.stage && (
									<div className="description">
										<Trans
											i18nKey="standard.singleStandard.subTitle"
											values={{
												currentStage: `${standardService.singleStandard.stage?.code} ${standardService.singleStandard.stage.name}`,
											}}>
											Current stage:
											<MarkedText>
												{standardService.singleStandard.stage?.code}{' '}
												{standardService.singleStandard.stage?.name}
											</MarkedText>
										</Trans>
									</div>
								)}
							</>
						)}

						<Form>
							<InputGroup
								title={t('standard.createNWP.general.title')}
								children={<CreateNewStandardEAFormGeneral />}
							/>
							<InputGroup
								title={t('standard.createNWPea.scopeSectionTitle')}
								children={<CreateNewStandardEAFormScopeJustification />}
							/>
							<InputGroup
								title={t('standard.createNWPea.referencesSectionTitle')}
								children={<CreateNewStandardEAReferences />}
							/>
							<InputGroup
								title={t('standard.createNWPea.patentsSectionTitle')}
								children={<CreateNewStandardEAPatents />}
							/>

							<InputGroup title={t('standard.createNWP.experts.title')} children={<Experts />} />

							<InputGroup
								title={t('standard.createNWPea.timelineTitle')}
								children={<CreateNewStandardEATimeline />}
							/>

							<InputGroup
								title={t('standard.createNWP.standardDraft.title')}
								children={<StandardDraft />}
							/>
							<InputGroup
								title={t('standard.createNWP.attachments.title')}
								children={<Attachments />}
							/>
							<InputGroup
								title={t('standard.createNWP.remarks.title')}
								children={<DraftRemarks />}
							/>
						</Form>
					</ContentContainer>
				);
			}}
		</Formik>
	);
});
